import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export interface ComboboxFilterOption {
  value: string;
  label: string;
  keywords?: string;
}

interface ComboboxFilterProps {
  title: string;
  value: string;
  options: ReadonlyArray<ComboboxFilterOption>;
  onValueChange: (nextValue: string) => void;
  clearOnReselect?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
}

export function ComboboxFilter({
  title,
  value,
  options,
  onValueChange,
  clearOnReselect = true,
  searchPlaceholder,
  emptyText = 'No results found.',
  className,
}: ComboboxFilterProps) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-expanded={open}
          className={cn('justify-between gap-2', className)}
        >
          <span>{title}</span>
          {selected && (
            <>
              <Separator orientation="vertical" className="h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {selected.label}
              </Badge>
            </>
          )}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[240px] p-0" collisionPadding={16}>
        <Command>
          <CommandInput
            placeholder={searchPlaceholder ?? `Search ${title.toLowerCase()}...`}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    if (clearOnReselect && currentValue === value) {
                      onValueChange('');
                    } else {
                      onValueChange(currentValue);
                    }
                    setOpen(false);
                  }}
                >
                  <span className="truncate">{option.label}</span>
                  <Check
                    className={cn('ml-auto opacity-0', option.value === value && 'opacity-100')}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
